import {useEffect, FC, useMemo} from 'react';
import {RouteComponentProps, Prompt, generatePath} from 'react-router-dom';
import {observer} from 'mobx-react-lite';
import {runInAction, when} from 'mobx';

import {AppBar, Button, Grid, IconButton, Tab, Tabs, Toolbar, Typography, Tooltip, makeStyles} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import LinkIcon from '@material-ui/icons/Link';
import LinkOffIcon from '@material-ui/icons/LinkOff';

import {FormDataPanel} from './Forms';
import RootStore from '../Core/mobx/RootStore';
import {AgentStore} from '../Agent/AgentStore';
import {CaseManagerStore} from './CaseManagerStore';
import {Alert, AlertTitle} from '@material-ui/lab';
import {checkPreQualFieldsComplete, getPreQualMissingFields} from './preQualValidation';

interface TabParams {
    tabIndex?: string;
    historyFormId?: string;
    email?: string;
}
interface FormModalProps extends RouteComponentProps<TabParams> {
    store: AgentStore | CaseManagerStore;
}

const TabMapping: Record<string, string> = {
    '0': 'prequal',
    '1': 'quote',
    '2': 'preapproval',
    '3': 'underwriting',
    '4': 'banking',
    '5': 'policy',
};

const useStyles = makeStyles({
    disabledTab: {
        opacity: 0.5,
        cursor: 'not-allowed',
        '& .MuiTab-wrapper': {
            pointerEvents: 'none',
        },
    },
    tooltip: {
        fontSize: '14px',
        backgroundColor: 'rgba(0, 0, 0, 0.87)',
        padding: '8px 12px',
    },
});

const FormModal: FC<FormModalProps> = observer(({store, match, history}) => {
    const classes = useStyles();
    const {familyFormId, familyEmail} = Object.fromEntries(new URLSearchParams(location.search));
    const {params} = match;
    const {tabIndex = '0', historyFormId, email} = params;
    const {
        parameterStore: {formDirty},
    } = store;

    const dirty = Array.from(formDirty.values()).includes(true);
    const {parameterStore, customerPortalStore} = RootStore.agentPortalStore;

    // Get missing fields for the tooltip
    const missingFields = getPreQualMissingFields(parameterStore);

    // Check if tabs should be disabled - removed useMemo to ensure it re-evaluates
    const shouldDisableTabs = (() => {
        // Determine if this is an old saved form vs a new unsaved form
        const isFormSaved = !!parameterStore.formid;

        let createdAfterCutoff = false;

        let createDate;
        if (parameterStore.created) {
            createDate = new Date(parameterStore.created);
        } else if (parameterStore.createTimestamp) {
            createDate = new Date(parameterStore.createTimestamp);
        }

        if (createDate) {
            createdAfterCutoff = createDate > new Date('2026-01-02');
        } else if (!isFormSaved) {
            createdAfterCutoff = true;
        } else {
            // Old saved form without created date - assume it was created before cutoff
            createdAfterCutoff = false;
        }

        // Check if case was NOT submitted online via Consumer
        // instantIulStartedByCustomer is set to true when submitted from customer portal
        const notSubmittedByConsumer = !parameterStore.instantIulStartedByCustomer;

        // Use detailed field checking to ensure all required fields are complete
        const preQualIncomplete = !checkPreQualFieldsComplete(parameterStore);

        return createdAfterCutoff && notSubmittedByConsumer && preQualIncomplete;
    })();

    const handleClose = () => {
        const path = historyFormId ? window.location.pathname.split('/', 6).join('/') : '/';
        history.push(path);
    };
    const openCustomerPortalData = (email: string) => {
        window.open(`/case-manager/customer-portal-data/?email=${email}`);
    };
    const openCustomerFormHistory = () => {
        const currentPath = window.location.pathname;
        const splitPath = currentPath.split('/');
        splitPath[splitPath.length - 1] = 'history';
        history.push(splitPath.join('/'));
    };

    const openCustomerSelection = () => {
        runInAction(() => {
            customerPortalStore.dialogOpen = true;
        });
    };

    const historyFormTabs = [{ariaLabel: 'preapproval-panel', label: 'Pre-approvals'}];

    const formTabs = [
        {ariaLabel: 'prequal-panel', label: 'Pre-qualification'},
        {ariaLabel: 'quotes-panel', label: 'Quotes'},
        {ariaLabel: 'preapproval-panel', label: 'Pre-approvals'},
        {ariaLabel: 'underwriting-panel', label: 'underwriting requirements'},
        {ariaLabel: 'banking-panel', label: 'banking information'},
        {ariaLabel: 'policy-panel', label: 'Policy Information'},
    ];

    useEffect(() => {
        if ((!familyFormId || !familyEmail) && !store.contactsFormStore.contact && !email) {
            RootStore.uiStore.showSnackbar('Please select a client first.', 'warning');
            history.push('/agent');
        }
    }, []);

    useEffect(() => {
        // If the form is saved and the URL contains '/agent/newform', redirect to the form page
        return when(
            () => !!parameterStore.updatedTimestamp && location.pathname.includes('/agent/newform'),
            () => {
                const currentUrl = location.pathname;

                // Define the pattern to look for '/agent/newform' in the URL
                const pattern = /\/agent\/newform/;

                // Replace with '/agent/form/${contact}/${formid}'
                const newUrl = currentUrl.replace(
                    pattern,
                    `/agent/form/${parameterStore.contact}/${parameterStore.formid}`,
                );
                history.replace(newUrl);
            },
        );
    }, []);

    return (
        <>
            <Prompt
                when={dirty}
                message={(location, action) => {
                    const pathParams = location.pathname.split('/');
                    const lastParam = pathParams[pathParams.length - 1];

                    if (lastParam.length === 0) {
                        const message = {
                            confirmText: 'Stay',
                            messageText: `It looks like you might have some unsaved changes with ${Array.from(
                                formDirty.keys(),
                            ).join(',')} form! Are you sure you want to continue?`,
                            cancelText: 'Leave',
                        };
                        return JSON.stringify(message);
                    }
                    if (lastParam !== tabIndex && formDirty.get(TabMapping[tabIndex])) {
                        const message = {
                            confirmText: 'Stay',
                            messageText: `It looks like you might have some unsaved changes with ${TabMapping[tabIndex]} form! Are you sure you want to continue?`,
                            cancelText: 'Leave',
                        };
                        return JSON.stringify(message);
                    }
                    return true;
                }}
            />
            <AppBar position="sticky">
                {!RootStore.currentAgentStates.includes(parameterStore.approval.state.value) && (
                    <Alert
                        severity="error"
                        style={{
                            backgroundColor: '#FF0000',
                            color: '#FFFFFF',
                        }}
                        iconMapping={{
                            error: <ErrorOutlineIcon style={{color: '#FFFFFF'}} />,
                        }}
                    >
                        <AlertTitle>Warning</AlertTitle>
                        <strong>
                            <>You are not licensed in the client's selected state!</>
                        </strong>
                    </Alert>
                )}
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
                        <CloseIcon />
                    </IconButton>
                    <Typography variant="h6" style={{marginLeft: 2, flex: 1}}>
                        Form: {store.contactsFormStore.contact}
                    </Typography>
                    {!historyFormId && (
                        <>
                            <Button
                                variant="contained"
                                style={{marginRight: '1em'}}
                                onClick={openCustomerSelection}
                                endIcon={
                                    customerPortalStore.selectedCustomer ? (
                                        <LinkIcon htmlColor="green" />
                                    ) : (
                                        <LinkOffIcon htmlColor="red" />
                                    )
                                }
                            >
                                Link Customer
                            </Button>
                            <Button
                                variant="contained"
                                style={{marginRight: '1em'}}
                                onClick={() =>
                                    openCustomerPortalData(encodeURIComponent(store.contactsFormStore.contact))
                                }
                            >
                                Data from Customer Portal
                            </Button>
                            <Button variant="contained" onClick={() => openCustomerFormHistory()}>
                                View History
                            </Button>
                        </>
                    )}
                    <Button autoFocus color="inherit" onClick={handleClose}>
                        Close
                    </Button>
                </Toolbar>
                <Tabs
                    value={parseInt(tabIndex, 10)}
                    centered
                    onChange={(_e, v) => {
                        // Prevent navigation to disabled tabs
                        if (shouldDisableTabs && (v === 1 || v === 2)) {
                            return;
                        }
                        const newPath = generatePath(match.path, {
                            ...match.params,
                            tabIndex: historyFormId ? v + 2 : v,
                        });
                        history.push(newPath);
                    }}
                    style={{padding: '30px 30px 0px 30px'}}
                >
                    {historyFormId
                        ? historyFormTabs.map((tab, index) => (
                              <Tab aria-label={tab.ariaLabel} label={tab.label} key={index} />
                          ))
                        : formTabs.map((tab, index) => {
                              const isDisabled = shouldDisableTabs && (index === 1 || index === 2);
                              const tabElement = (
                                  <Tab
                                      aria-label={tab.ariaLabel}
                                      label={tab.label}
                                      key={index}
                                      disabled={isDisabled}
                                      className={isDisabled ? classes.disabledTab : ''}
                                  />
                              );

                              if (isDisabled) {
                                  // Format field names for display
                                  const formatFieldName = (name: string) => {
                                      // Convert camelCase to Title Case with spaces
                                      return name
                                          .replace(/([A-Z])/g, ' $1')
                                          .replace(/^./, (str) => str.toUpperCase())
                                          .trim();
                                  };

                                  const tooltipContent = (
                                      <div>
                                          <div>Please complete all required fields in Pre-Qualification Tab.</div>
                                          {missingFields.length > 0 && (
                                              <>
                                                  <div style={{marginTop: '8px'}}>
                                                      <strong>Missing fields:</strong>
                                                  </div>
                                                  <ul style={{margin: '4px 0', paddingLeft: '20px'}}>
                                                      {missingFields.slice(0, 10).map((field, idx) => (
                                                          <li key={idx}>{formatFieldName(field.name)}</li>
                                                      ))}
                                                      {missingFields.length > 10 && (
                                                          <li>...and {missingFields.length - 10} more</li>
                                                      )}
                                                  </ul>
                                              </>
                                          )}
                                      </div>
                                  );

                                  return (
                                      <Tooltip
                                          key={index}
                                          title={tooltipContent}
                                          classes={{tooltip: classes.tooltip}}
                                          arrow
                                      >
                                          <span>{tabElement}</span>
                                      </Tooltip>
                                  );
                              }

                              return tabElement;
                          })}
                </Tabs>
            </AppBar>
            <Grid container style={{flex: 1}}>
                <Grid item xs={12} style={{display: 'flex', paddingTop: 30}}>
                    <FormDataPanel selectedTabIndex={parseInt(tabIndex, 10)} store={store} />
                </Grid>
            </Grid>
        </>
    );
});

FormModal.displayName = 'FormModal';

export default FormModal;
